import { memo, useContext, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { CopilotContext } from '@Sage/ssm-web5';

const EntityCrudContent = ({ children, ...props }) => {
    const { copilotBarProps } = useContext(CopilotContext);
    const {
        height: copilotBarHeight,
        isMinimized: isCopilotBarMinimized,
        isOpen: isCopilotBarOpen,
        padding: copilotBarPadding,
    } = copilotBarProps;

    const containerRef = useRef(null);
    const previousBottomInsetRef = useRef(null);

    const copilotBottomInset = useMemo(() => {
        if (!isCopilotBarOpen || isCopilotBarMinimized) {
            return 0;
        }
        return copilotBarPadding * 2 + copilotBarHeight;
    }, [copilotBarHeight, copilotBarPadding, isCopilotBarMinimized, isCopilotBarOpen]);

    const bottomSpacing = props.bottomSpacing || 0;
    const includeCopilotInBottomInset = props.includeCopilotInBottomInset ?? true;
    const totalBottomInset = useMemo(() => {
        const copilotPart = includeCopilotInBottomInset ? copilotBottomInset : 0;
        return copilotPart + bottomSpacing;
    }, [bottomSpacing, copilotBottomInset, includeCopilotInBottomInset]);

    const shouldApplyBottomInset = totalBottomInset > 0;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const previousBottomInset = previousBottomInsetRef.current;
        const currentBottomInset = shouldApplyBottomInset ? totalBottomInset : 0;

        if (previousBottomInset !== null && previousBottomInset !== currentBottomInset) {
            const { scrollHeight, scrollTop, clientHeight } = container;
            const wasAtBottom = scrollHeight - scrollTop - clientHeight < 10;

            if (wasAtBottom) {
                requestAnimationFrame(() => {
                    if (container) {
                        container.scrollTop = container.scrollHeight;
                    }
                });
            }
        }

        previousBottomInsetRef.current = currentBottomInset;
    }, [shouldApplyBottomInset, totalBottomInset]);

    const finalProps = useMemo(() => {
        const restProps = { ...props };
        delete restProps.ref;
        delete restProps.bottomSpacing;
        delete restProps.includeCopilotInBottomInset;
        delete restProps.style;

        const mergedStyle = { ...(props.style || {}) };
        if (shouldApplyBottomInset) {
            // Drives height + margin-bottom in SCSS so fixed calc(100% - 64px) does not overflow the wrapper.
            mergedStyle['--entity-crud-content-bottom-inset'] = `${totalBottomInset}px`;
        }
        const style = Object.keys(mergedStyle).length ? mergedStyle : null;

        return {
            ...restProps,
            className: classNames('entity-crud-content', props.className),
            style,
        };
    }, [props, shouldApplyBottomInset, totalBottomInset]);

    return (
        <div {...finalProps} ref={containerRef}>
            {children}
        </div>
    );
};

export default memo(EntityCrudContent);
